import { useState, useEffect, useCallback } from 'react';
import { finance, hrms, getFileUrl } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import ClaimDetailModal from '../../components/common/ClaimDetailModal';
import { theme } from '../../theme';

function FinanceClaims() {
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);

  // Merge state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeTitle, setMergeTitle] = useState('');
  const [mergeSaving, setMergeSaving] = useState(false);

  // View modal (read-only, all statuses)
  const [viewClaim, setViewClaim] = useState(null);

  // Verify modal
  const [verifyModal, setVerifyModal] = useState(null); // full claim detail
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [itemAmounts, setItemAmounts] = useState({}); // { itemId: approvedAmount }
  const [verifyRemarks, setVerifyRemarks] = useState('');

  // Reject modal
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    hrms.getEmployees({ limit: 500 }).then(r => {
      const d = r.data;
      setEmployees(Array.isArray(d) ? d : d.items || d.results || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      if (filterEmployeeId) params.employee_id = filterEmployeeId;
      const r = await finance.getFinanceClaims(params);
      const d = r.data;
      setClaimsData(Array.isArray(d) ? d : d.items || []);
    } catch { setClaimsData([]); } finally { setLoading(false); }
  }, [filterStatus, filterMonth, filterYear, filterEmployeeId]);

  useEffect(() => { load(); }, [load]);

  const openView = async (id) => {
    try {
      const r = await finance.getClaimDetail(id);
      setViewClaim(r.data);
    } catch { alert('Failed to load claim details.'); }
  };

  const openVerify = async (row) => {
    setVerifyLoading(true);
    setVerifyRemarks('');
    try {
      const r = await finance.getClaimDetail(row.id);
      const detail = r.data;
      setVerifyModal(detail);
      const amounts = {};
      (detail.items || []).forEach((i) => {
        amounts[i.id] = i.approved_amount ?? i.amount;
      });
      setItemAmounts(amounts);
    } catch { alert('Failed to load claim details.'); }
    finally { setVerifyLoading(false); }
  };

  const handleVerifyApprove = async () => {
    setSaving(true);
    try {
      const items = (verifyModal.items || []).map((i) => ({
        id: i.id,
        approved_amount: Number(itemAmounts[i.id] ?? i.amount),
      }));
      await finance.verifyClaim(verifyModal.id, { items, remarks: verifyRemarks });
      setVerifyModal(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to approve.');
    } finally { setSaving(false); }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Mark this claim as Paid?')) return;
    try {
      await finance.markClaimPaid(id);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to mark as paid.');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Please enter a rejection reason.'); return; }
    setSaving(true);
    try {
      await finance.rejectClaim(rejectModal, rejectReason.trim());
      setRejectModal(null); setRejectReason(''); load();
    } catch { alert('Failed to reject.'); } finally { setSaving(false); }
  };

  const handleMerge = async () => {
    setMergeSaving(true);
    try {
      await finance.mergeClaims({ claim_ids: Array.from(selectedIds), title: mergeTitle || undefined });
      setSelectedIds(new Set()); setMergeModal(false); setMergeTitle(''); load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to merge claims.'); }
    finally { setMergeSaving(false); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectedClaims = claimsData.filter(c => selectedIds.has(c.id));
  const selectedEmployeeIds = new Set(selectedClaims.map(c => c.employee_id));
  const canMerge = selectedIds.size >= 2 && selectedEmployeeIds.size === 1
    && selectedClaims.every(c => ['submitted', 'under_review', 'draft'].includes(c.status));

  const approvedTotal = verifyModal
    ? (verifyModal.items || []).reduce((s, i) => s + (Number(itemAmounts[i.id]) || 0), 0)
    : 0;

  const columns = [
    {
      header: '', key: 'id', width: '40px',
      render: (id, row) => ['submitted', 'under_review', 'draft'].includes(row.status) ? (
        <input type="checkbox" checked={selectedIds.has(id)} onChange={() => toggleSelect(id)}
          style={{ width: 15, height: 15, cursor: 'pointer' }} />
      ) : null,
    },
    { header: 'Claim #', key: 'claim_code', render: (v, row) => v || `#${row.id}` },
    { header: 'Employee', key: 'employee_name', render: (v) => v || '—' },
    { header: 'Title', key: 'title' },
    { header: 'Project', key: 'project_name', render: (v) => v || '—' },
    { header: 'Total Amount', key: 'total_amount', render: (v) => `₹${Number(v || 0).toLocaleString()}` },
    { header: 'Submitted', key: 'submitted_at', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v || 'submitted'} byName={row.reviewed_by_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button size="sm" variant="outlineGray" onClick={() => openView(id)}>View</Button>
          {(row.status === 'submitted' || row.status === 'under_review') && (
            <>
              <Button size="sm" variant="primary" onClick={() => openVerify(row)}>Verify</Button>
              <Button size="sm" variant="danger" onClick={() => { setRejectModal(id); setRejectReason(''); }}>Reject</Button>
            </>
          )}
          {row.status === 'approved' && (
            <Button size="sm" variant="success" onClick={() => handleMarkPaid(id)}>Mark Paid</Button>
          )}
          {row.status === 'paid' && (
            <span style={{ fontSize: '0.75rem', color: theme.success, fontWeight: 600 }}>✓ Paid</span>
          )}
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Claims Review</h1>
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#EFF6FF', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #BFDBFE' }}>
            <span style={{ fontSize: '0.85rem', color: '#1D4ED8', fontWeight: 600 }}>{selectedIds.size} selected</span>
            {!canMerge && selectedEmployeeIds.size > 1 && (
              <span style={{ fontSize: '0.75rem', color: theme.danger }}>⚠ Different employees — cannot merge</span>
            )}
            {canMerge && (
              <Button size="sm" variant="primary" onClick={() => setMergeModal(true)}>Merge Claims</Button>
            )}
            <Button size="sm" variant="outlineGray" onClick={() => setSelectedIds(new Set())}>✕ Clear</Button>
          </div>
        )}
      </div>

      <Card padding="1rem">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Employee type-search */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              list="fc-emp-list"
              placeholder="Search employee…"
              value={filterEmployee}
              onChange={(e) => {
                setFilterEmployee(e.target.value);
                const match = employees.find(emp => emp.full_name === e.target.value);
                setFilterEmployeeId(match ? String(match.id) : '');
              }}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', minWidth: 200 }}
            />
            <datalist id="fc-emp-list">
              {employees.map(e => <option key={e.id} value={e.full_name}>{e.emp_code} — {e.full_name}</option>)}
            </datalist>
            {filterEmployee && (
              <button onClick={() => { setFilterEmployee(''); setFilterEmployeeId(''); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '0.9rem' }}>✕</button>
            )}
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            <option value="">All Status</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            <option value="">All Months</option>
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            <option value="">All Years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="outlineGray" size="sm" onClick={() => { setFilterStatus(''); setFilterMonth(''); setFilterYear(''); setFilterEmployee(''); setFilterEmployeeId(''); }}>Clear</Button>
        </div>
      </Card>

      <Card padding="0">
        <Table columns={columns} data={claimsData} loading={loading} emptyMessage="No claims found" />
      </Card>

      {/* ── Verify Modal ── */}
      <Modal
        isOpen={!!verifyModal}
        onClose={() => setVerifyModal(null)}
        title={verifyModal ? `Verify Claim: ${verifyModal.claim_code || `#${verifyModal.id}`}` : ''}
        maxWidth="680px"
      >
        {verifyLoading ? (
          <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0' }}>Loading...</p>
        ) : verifyModal && (
          <>
            {/* Claim meta */}
            <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem' }}>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>Employee</span><p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.875rem' }}>{verifyModal.employee_name}</p></div>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>Project</span><p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.875rem' }}>{verifyModal.project_name || '—'}</p></div>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>Submitted</span><p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.875rem' }}>{verifyModal.submitted_at ? new Date(verifyModal.submitted_at).toLocaleDateString() : '—'}</p></div>
              <div><span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>Submitted Amount</span><p style={{ margin: '0.1rem 0 0', fontWeight: 700, fontSize: '0.95rem', color: theme.primary }}>₹{Number(verifyModal.total_amount || 0).toLocaleString()}</p></div>
              {verifyModal.description && (
                <div style={{ gridColumn: '1 / -1' }}><span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>Description</span><p style={{ margin: '0.1rem 0 0', fontSize: '0.85rem' }}>{verifyModal.description}</p></div>
              )}
            </div>

            {/* Expense items */}
            <p style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.text, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Expense Items</p>
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '1.25rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    {['Description', 'Category', 'Date', 'Submitted (₹)', 'Approved (₹)', 'Receipt'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap', fontSize: '0.72rem', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(verifyModal.items || []).map((item) => (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.text }}>
                        {item.description}
                        {item.bill_available === false ? (
                          <span style={{ display: 'inline-block', marginTop: '0.2rem', fontSize: '0.68rem', fontWeight: 700, background: '#FEF3C7', color: '#92400E', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', border: '1px solid #FDE68A' }}>No Bill</span>
                        ) : item.bill_file_path ? (
                          <a href={getFileUrl(item.bill_file_path)} target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: '0.7rem', color: theme.primary, marginTop: '0.15rem' }}>📎 View Receipt</a>
                        ) : (
                          <span style={{ display: 'block', fontSize: '0.7rem', color: theme.danger, marginTop: '0.15rem' }}>⚠ No receipt</span>
                        )}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted, textTransform: 'capitalize' }}>{item.category}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>{item.expense_date ? new Date(item.expense_date + 'T12:00:00').toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.text, fontWeight: 600 }}>₹{Number(item.amount).toLocaleString()}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <input
                          type="number"
                          min="0"
                          value={itemAmounts[item.id] ?? item.amount}
                          onChange={(e) => setItemAmounts((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          style={{ ...inp, width: '100px', padding: '0.35rem 0.5rem', fontWeight: 600 }}
                        />
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>
                        {item.bill_available === false ? (
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#FEF3C7', color: '#92400E', borderRadius: '0.25rem', padding: '0.15rem 0.45rem', border: '1px solid #FDE68A' }}>No Bill</span>
                        ) : item.bill_file_path ? (
                          <a href={getFileUrl(item.bill_file_path)} target="_blank" rel="noreferrer" style={{ color: theme.primary, fontSize: '0.75rem', fontWeight: 600 }}>📎 View</a>
                        ) : (
                          <span style={{ color: theme.textMuted }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Approved total */}
            <div style={{ background: '#F0FDF4', border: `1px solid #BBF7D0`, borderRadius: '0.5rem', padding: '0.875rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 600, color: theme.text }}>Total Approved Amount</span>
              <span style={{ fontSize: '1.15rem', fontWeight: 700, color: theme.success }}>₹{approvedTotal.toLocaleString()}</span>
            </div>

            {/* Remarks */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={lbl}>Remarks (optional)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={verifyRemarks} onChange={(e) => setVerifyRemarks(e.target.value)} placeholder="Add any approval notes..." />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button variant="outlineGray" onClick={() => setVerifyModal(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => { setRejectModal(verifyModal.id); setVerifyModal(null); setRejectReason(''); }}>Reject</Button>
              <Button variant="success" loading={saving} onClick={handleVerifyApprove}>Approve ₹{approvedTotal.toLocaleString()}</Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── View Modal (read-only) ── */}
      <ClaimDetailModal claim={viewClaim} onClose={() => setViewClaim(null)} />

      {/* ── Reject Modal ── */}
      <Modal isOpen={!!rejectModal} onClose={() => { setRejectModal(null); setRejectReason(''); }} title="Reject Claim"
        footer={<><Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button><Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button></>}>
        <label style={lbl}>Reason for Rejection <span style={{ color: theme.danger }}>*</span></label>
        <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: '0 0 0.5rem' }}>
          This will be visible to the employee and they can edit and resubmit the claim.
        </p>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '100px', borderColor: rejectReason.trim() ? theme.border : theme.danger }} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Missing receipt for item #2, amount exceeds policy limit..." />
      </Modal>

      {/* ── Merge Modal ── */}
      <Modal isOpen={mergeModal} onClose={() => { setMergeModal(false); setMergeTitle(''); }} title="Merge Claims"
        footer={<><Button variant="outlineGray" onClick={() => setMergeModal(false)}>Cancel</Button><Button variant="primary" loading={mergeSaving} onClick={handleMerge}>Merge {selectedIds.size} Claims</Button></>}>
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.85rem', color: theme.textMuted, margin: '0 0 0.75rem' }}>
            Merging the following claims into one:
          </p>
          {selectedClaims.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.75rem', background: theme.pageBg, borderRadius: '0.375rem', marginBottom: '0.35rem', fontSize: '0.85rem' }}>
              <span style={{ fontWeight: 600 }}>{c.claim_code}</span>
              <span style={{ color: theme.textMuted }}>{c.title}</span>
              <span style={{ color: theme.primary, fontWeight: 600 }}>₹{Number(c.total_amount || 0).toLocaleString()}</span>
            </div>
          ))}
          <p style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.text, margin: '0.75rem 0 0.25rem' }}>
            Total: ₹{selectedClaims.reduce((s, c) => s + Number(c.total_amount || 0), 0).toLocaleString()}
          </p>
        </div>
        <label style={lbl}>Merged Claim Title (optional)</label>
        <input style={{ ...inp, marginTop: '0.25rem' }} value={mergeTitle} onChange={e => setMergeTitle(e.target.value)} placeholder="Leave blank to auto-generate" />
        <p style={{ fontSize: '0.75rem', color: theme.danger, margin: '0.5rem 0 0' }}>
          ⚠ The original claims will be cancelled and replaced with the merged claim.
        </p>
      </Modal>
    </div>
  );
}

export default FinanceClaims;
